import { createMockReports, MOCK_REPORT_DATASET_VERSION } from "./src/hex-poc/mock-reports.js";
import { createMockOverlayReports } from "./src/hex-poc/mock-overlay-reports.js";
import { initializeHexPocMap } from "./src/hex-poc/map.js";
import { HEX_OVERLAY_CATEGORIES } from "./src/hex-poc/overlay-config.js";
import {
  closestQuantityThresholdHandle,
  quantityCountForPointerPosition,
} from "./src/hex-poc/quantity-styling.js";
import { createHexQuantityScale } from "./src/hex-poc/quantity-scale.js";
import {
  filterHexReportsBySymptoms,
  hexSymptomSelectionKey,
  HEX_POC_SYMPTOMS,
  normalizeHexSymptomSelection,
} from "./src/hex-poc/symptoms.js";
import { closestRangeHandle, createHexTimeController, sliderIndexForPointerPosition } from "./src/hex-poc/time-controller.js";
import { discoverHexReportTimeline, filterHexReportsByTime, formatHexReportDate } from "./src/hex-poc/time-series.js";
import { communityReportCopy, countCommunityReportCopy, formatCommunityReportCopy } from "./community-report-copy.js";

const reports = createMockReports();
const overlayReports = createMockOverlayReports();
const screen = document.getElementById("hexScreen");
const pageParams = new URLSearchParams(window.location.search);
const debugMap = pageParams.get("debugMap") === "1" || pageParams.get("debugMode") === "1";
const debugMode = pageParams.get("debug") === "1" || debugMap;
const language = localStorage.getItem("community-report-lang") === "ES" ? "ES" : "EN";
document.documentElement.lang = language === "ES" ? "es" : "en";
const copy = (key) => communityReportCopy(language, key);
const formatCopy = (key, replacements) => formatCommunityReportCopy(language, key, replacements);
const countCopy = (oneKey, manyKey, count, replacements = {}) => countCommunityReportCopy(
  language, oneKey, manyKey, count, replacements,
);
const formatDate = (date) => formatHexReportDate(date, language);
const timeline = discoverHexReportTimeline(reports);
let selectedSymptoms = normalizeHexSymptomSelection([]);
let selectedSymptomsKey = hexSymptomSelectionKey(selectedSymptoms);
let renderMapForFilters = () => {};
let setMapOverlayVisibility = () => {};
let setMapQuantityThresholds = () => {};
const quantityScale = createHexQuantityScale();
const timeController = createHexTimeController({
  timeline,
  onChange: (state, reason) => {
    syncHexTimeControls(state);
    if (reason === "playback") return;
    refreshQuantityScale(state);
    renderMapForFilters();
  },
});
quantityScale.refresh({
  reports,
  datasetVersion: MOCK_REPORT_DATASET_VERSION,
  timeState: timeController.state,
  selectedSymptoms,
});

screen.innerHTML = `
  <section class="hex-poc-panel" aria-label="${copy("hex_page_aria")}">
    <div class="map-heading">
      <div>
        <p class="eyebrow">${copy("hex_preview")}</p>
        <h1>${copy("hex_title")}</h1>
      </div>
      <div class="hex-poc-heading-actions">
        <a class="hex-poc-home-link" href="/data">${copy("hex_data")}</a>
        <span class="map-count" data-hex-visible-count>${visibleReportCountMarkup()}</span>
      </div>
    </div>
    <p class="hex-poc-intro">${copy("hex_intro")}</p>
    <div class="hex-poc-controls-panel" aria-label="${copy("hex_controls_aria")}">
      <div data-hex-time-controls></div>
      <section class="hex-poc-controls-section hex-poc-human-controls" aria-labelledby="hexPocHumanHeading">
        <h2 class="hex-poc-control-heading" id="hexPocHumanHeading">${copy("hex_symptom_selector")}</h2>
        <fieldset class="hex-poc-symptom-filter" aria-describedby="hexPocSymptomInstruction">
          <legend>${copy("hex_symptoms")}</legend>
          <p class="hex-poc-symptom-instruction" id="hexPocSymptomInstruction">${copy("hex_symptom_instruction")}</p>
          <div class="hex-poc-symptom-options" aria-label="${copy("hex_required_symptoms")}">
            ${hexSymptomButtonsMarkup()}
          </div>
          <div class="hex-poc-symptom-status">
            <button class="hex-poc-clear-symptoms" type="button" data-hex-clear-symptoms disabled>${copy("hex_clear_symptoms")}</button>
            <span data-hex-symptom-context role="status" aria-live="polite">${symptomContextText()}</span>
          </div>
        </fieldset>
        <p class="hex-poc-time-empty" data-hex-human-empty role="status" hidden>${emptyTimeMessage()}</p>
      </section>
      <section class="hex-poc-controls-section hex-poc-layer-controls" aria-labelledby="hexPocLayersHeading">
        <h2 class="hex-poc-control-heading" id="hexPocLayersHeading">${copy("hex_map_layers")}</h2>
        <div class="hex-poc-layer-groups">
          <fieldset class="hex-poc-layer-group">
            <legend>${copy("hex_human")}</legend>
            <div class="hex-poc-display-controls">
              <button type="button" data-hex-display="showShading" aria-pressed="true">${copy("hex_sick_shading")}</button>
              <button type="button" data-hex-display="showDots" aria-pressed="true">${copy("hex_report_dots")}</button>
            </div>
          </fieldset>
          <fieldset class="hex-poc-layer-group">
            <legend>${copy("hex_additional_reports")}</legend>
            <div class="hex-poc-overlay-controls">
              <button type="button" data-hex-overlay="animals" aria-pressed="false">${overlaySwatchMarkup("animals")}${copy("hex_animal_reports")}</button>
              <button type="button" data-hex-overlay="environment" aria-pressed="false">${overlaySwatchMarkup("environment")}${copy("hex_environment_reports")}</button>
            </div>
          </fieldset>
        </div>
        ${hexQuantityThresholdControlsMarkup()}
      </section>
    </div>
    <div class="hex-poc-map" id="hexPocMap" aria-label="${copy("hex_map_aria")}"></div>
    ${mapZoomDebugMarkup("hexPocMap")}
    <div class="hex-poc-legend" aria-label="${copy("hex_legend_aria")}">
      ${hexQuantityLegendMarkup()}
      <span><i class="hex-poc-legend-report-dot" aria-hidden="true"></i>${copy("hex_larger_dots")}</span>
      <span>${overlaySwatchMarkup("animals")}${copy("hex_animal_reports")}</span>
      <span>${overlaySwatchMarkup("environment")}${copy("hex_environment_reports")}</span>
      <span data-hex-resolution>${formatCopy("hex_resolution", { resolution: "--" })}</span>
    </div>
    <p class="map-note">${copy("hex_click_note")}</p>
  </section>
`;

renderHexTimeControls();

const displayOptions = { showShading: true, showDots: true };
const overlayVisibility = { animals: false, environment: false };
let setMapDisplayOptions = () => {};
screen.addEventListener("click", (event) => {
  const displayButton = event.target.closest("[data-hex-display]");
  if (displayButton) {
    const option = displayButton.dataset.hexDisplay;
    displayOptions[option] = !displayOptions[option];
    displayButton.setAttribute("aria-pressed", String(displayOptions[option]));
    setMapDisplayOptions(displayOptions);
    return;
  }
  const overlayButton = event.target.closest("[data-hex-overlay]");
  if (!overlayButton) return;
  const category = overlayButton.dataset.hexOverlay;
  overlayVisibility[category] = !overlayVisibility[category];
  overlayButton.setAttribute("aria-pressed", String(overlayVisibility[category]));
  setMapOverlayVisibility(overlayVisibility);
});

screen.addEventListener("click", (event) => {
  const button = event.target.closest("button");
  if (!button) return;
  if (button.dataset.hexSymptom) {
    toggleHexSymptom(button.dataset.hexSymptom);
    return;
  }
  if (button.hasAttribute("data-hex-clear-symptoms")) {
    setSelectedSymptoms([]);
    return;
  }
  if (button.dataset.hexTimeAction === "play-pause") timeController.playPause();
});

screen.addEventListener("input", (event) => {
  const target = event.target;
  if (target.matches("[data-hex-quantity-threshold]")) {
    setHexQuantityThreshold(target.dataset.hexQuantityThreshold, target.value);
    return;
  }
  if (!target.matches("[data-hex-time-slider]")) return;
  const changed = target.dataset.hexTimeSlider === "start"
    ? timeController.selectStartIndex(target.value)
    : timeController.selectIndex(target.value);
  if (!changed) syncHexTimeControls(timeController.state);
});

screen.addEventListener("pointerdown", (event) => {
  const rangeControl = event.target.closest(".hex-poc-quantity-range-control");
  if (!rangeControl) return;
  event.preventDefault();
  const { maximum, thresholds } = quantityScale.state;
  const handle = closestQuantityThresholdHandle(
    quantityCountForPointerPosition(
      event.clientX,
      rangeControl.getBoundingClientRect(),
      maximum,
    ),
    thresholds,
  );
  const updateQuantityHandle = (pointerEvent) => {
    const count = quantityCountForPointerPosition(
      pointerEvent.clientX,
      rangeControl.getBoundingClientRect(),
      quantityScale.state.maximum,
    );
    setHexQuantityThreshold(handle, count);
  };
  const finish = () => {
    window.removeEventListener("pointermove", updateQuantityHandle);
    window.removeEventListener("pointerup", finish);
    window.removeEventListener("pointercancel", finish);
  };
  window.addEventListener("pointermove", updateQuantityHandle);
  window.addEventListener("pointerup", finish);
  window.addEventListener("pointercancel", finish);
  updateQuantityHandle(event);
});

screen.addEventListener("pointerdown", (event) => {
  const rangeControl = event.target.closest(".hex-poc-date-range-control");
  if (!rangeControl) return;
  event.preventDefault();
  const maximumIndex = timeline.days.length - 1;
  const handle = closestRangeHandle(
    sliderIndexForPointerPosition(event.clientX, rangeControl.getBoundingClientRect(), maximumIndex),
    timeController.state,
  );
  const updateRangeHandle = (pointerEvent) => {
    const index = sliderIndexForPointerPosition(pointerEvent.clientX, rangeControl.getBoundingClientRect(), maximumIndex);
    if (handle === "start") timeController.selectStartIndex(index);
    else timeController.selectIndex(index);
  };
  const finish = () => {
    window.removeEventListener("pointermove", updateRangeHandle);
    window.removeEventListener("pointerup", finish);
    window.removeEventListener("pointercancel", finish);
  };
  window.addEventListener("pointermove", updateRangeHandle);
  window.addEventListener("pointerup", finish);
  window.addEventListener("pointercancel", finish);
  updateRangeHandle(event);
});

initializeHexPocMap({
  elementId: "hexPocMap",
  reports,
  overlayReports,
  onResolutionChange: (resolution) => {
    document.querySelectorAll("[data-hex-resolution]").forEach((element) => {
      element.textContent = formatCopy("hex_resolution", { resolution });
    });
  },
  onZoomChange: (zoom) => updateMapZoomDebug("hexPocMap", zoom),
  getTimeState: () => timeController.state,
  getSelectedSymptoms: () => selectedSymptoms,
  getLanguage: () => language,
  mapFallbackCopy: copy,
  getQuantityThresholds: () => quantityScale.state.thresholds,
  onQuantityThresholdsReady: (setThresholds) => {
    setMapQuantityThresholds = setThresholds;
  },
  onTimeRendererReady: (render) => {
    renderMapForFilters = render;
    renderMapForFilters();
  },
  onDisplayOptionsReady: (setOptions) => {
    setMapDisplayOptions = setOptions;
    setMapDisplayOptions(displayOptions);
  },
  getOverlayVisibility: () => overlayVisibility,
  onOverlayVisibilityReady: (setVisibility) => {
    setMapOverlayVisibility = setVisibility;
    setMapOverlayVisibility(overlayVisibility);
  },
}).catch((error) => {
  const element = document.getElementById("hexPocMap");
  if (element) {
    element.innerHTML = `<div class="map-fallback"><strong>${copy("hex_map_unavailable")}</strong><span>${error.message}</span></div>`;
  }
});

function renderHexTimeControls() {
  const node = screen.querySelector("[data-hex-time-controls]");
  if (!node) return;
  const state = timeController.state;
  const reportCount = filteredHumanReports(state).length;
  syncVisibleHumanReportCount(reportCount);
  syncSymptomContext(reportCount);
  node.innerHTML = `
    <section class="hex-poc-controls-section hex-poc-time-controls" aria-labelledby="hexPocTimeHeading">
      <div class="hex-poc-control-heading-row">
        <h2 class="hex-poc-control-heading" id="hexPocTimeHeading">${copy("hex_time_window")}</h2>
        <p class="hex-poc-time-summary" data-hex-time-summary aria-live="polite">${timeWindowSummaryText(state, reportCount)}</p>
      </div>
      <div class="hex-poc-time-actions">
        <p class="hex-poc-time-behavior">${copy("hex_time_behavior")}</p>
        <button class="hex-poc-playback-button" type="button" data-hex-time-action="play-pause" aria-label="${copy(state.isPlaying ? "hex_pause_playback" : "hex_play_playback")}" aria-pressed="${state.isPlaying}">${copy(state.isPlaying ? "hex_pause" : "hex_play")}</button>
      </div>
      <div class="hex-poc-date-slider">
        ${hexTimeRangeSliderMarkup(state)}
        <div class="hex-poc-time-range"><span>${formatDate(timeline.startDate)}</span><span>${formatDate(timeline.endDate)}</span></div>
      </div>
    </section>
  `;
  syncHumanEmptyState(reportCount);
}

function hexTimeRangeSliderMarkup(state) {
  return `
    <div class="hex-poc-time-slider-heading">
      <span>${copy("hex_active_start")} <strong data-hex-time-start-label>${formatDate(state.startDate)}</strong></span>
      <span>${copy("hex_selected_end")} <strong data-hex-time-selected-label>${formatDate(state.selectedDate)}</strong></span>
    </div>
    <div class="hex-poc-date-range-control" style="--range-start: ${(state.startIndex / (timeline.days.length - 1)) * 100}%; --range-end: ${(state.selectedIndex / (timeline.days.length - 1)) * 100}%">
      <input type="range" min="0" max="${timeline.days.length - 1}" value="${state.startIndex}" aria-label="${copy("hex_start_date_aria")}" aria-valuetext="${formatDate(state.startDate)}" data-hex-time-slider="start">
      <input class="range-end" type="range" min="0" max="${timeline.days.length - 1}" value="${state.selectedIndex}" aria-label="${copy("hex_selected_end_date_aria")}" aria-valuetext="${formatDate(state.selectedDate)}" data-hex-time-slider="selected">
    </div>
  `;
}

function syncHexTimeControls(state) {
  const node = screen.querySelector("[data-hex-time-controls]");
  if (!node) return;
  const reportCount = filteredHumanReports(state).length;
  syncVisibleHumanReportCount(reportCount);
  syncSymptomContext(reportCount);
  const selectedDateLabel = formatDate(state.selectedDate);
  const startDateLabel = formatDate(state.startDate);
  node.querySelector("[data-hex-time-summary]").textContent = timeWindowSummaryText(state, reportCount);
  node.querySelectorAll("[data-hex-time-selected-label]").forEach((element) => { element.textContent = selectedDateLabel; });
  node.querySelectorAll("[data-hex-time-start-label]").forEach((element) => { element.textContent = startDateLabel; });
  node.querySelectorAll("[data-hex-time-slider]").forEach((slider) => {
    const isStartSlider = slider.dataset.hexTimeSlider === "start";
    slider.value = String(isStartSlider ? state.startIndex : state.selectedIndex);
    slider.setAttribute("aria-valuetext", isStartSlider ? startDateLabel : selectedDateLabel);
  });
  const rangeControl = node.querySelector(".hex-poc-date-range-control");
  if (rangeControl) {
    rangeControl.style.setProperty("--range-start", `${(state.startIndex / (timeline.days.length - 1)) * 100}%`);
    rangeControl.style.setProperty("--range-end", `${(state.selectedIndex / (timeline.days.length - 1)) * 100}%`);
  }
  const playButton = node.querySelector("[data-hex-time-action=\"play-pause\"]");
  playButton.textContent = copy(state.isPlaying ? "hex_pause" : "hex_play");
  playButton.setAttribute("aria-label", copy(state.isPlaying ? "hex_pause_playback" : "hex_play_playback"));
  playButton.setAttribute("aria-pressed", String(state.isPlaying));
  syncHumanEmptyState(reportCount);
}

function emptyTimeMessage() {
  if (selectedSymptoms.length === 1) {
    return formatCopy("hex_empty_one_symptom", { symptom: symptomLabel(selectedSymptoms[0]) });
  }
  if (selectedSymptoms.length > 1) {
    return copy("hex_empty_many_symptoms");
  }
  return copy("hex_empty");
}

function hexQuantityThresholdControlsMarkup() {
  const { maximum, thresholds } = quantityScale.state;
  return `
    <section class="hex-poc-quantity-controls" aria-label="${copy("hex_thresholds_aria")}">
      <div class="hex-poc-quantity-heading">
        <p>${copy("hex_thresholds")}</p>
        <span>${copy("hex_threshold_instruction")}</span>
      </div>
      <div class="hex-poc-quantity-cutoff-labels">
        <span>${formatCopy("hex_yellow_orange", { count: `<output data-hex-lower-threshold>${formatSickReportCount(thresholds.lowerCount)}</output>` })}</span>
        <span>${formatCopy("hex_orange_red", { count: `<output data-hex-upper-threshold>${formatSickReportCount(thresholds.upperCount)}</output>` })}</span>
      </div>
      <div class="hex-poc-quantity-range-control" style="--lower-stop: ${quantityThresholdPosition(thresholds.lowerCount, maximum)}%; --upper-stop: ${quantityThresholdPosition(thresholds.upperCount, maximum)}%">
        <input class="hex-poc-quantity-lower-handle" type="range" min="0" max="${maximum}" step="1" value="${thresholds.lowerCount}" aria-label="${copy("hex_yellow_orange_aria")}" aria-valuetext="${formatSickReportCount(thresholds.lowerCount)}" data-hex-quantity-threshold="lowerCount">
        <input class="hex-poc-quantity-upper-handle" type="range" min="0" max="${maximum}" step="1" value="${thresholds.upperCount}" aria-label="${copy("hex_orange_red_aria")}" aria-valuetext="${formatSickReportCount(thresholds.upperCount)}" data-hex-quantity-threshold="upperCount">
      </div>
    </section>
  `;
}

function hexQuantityLegendMarkup() {
  const { thresholds } = quantityScale.state;
  return `
    <span><i class="hex-poc-legend-quantity hex-poc-legend-quantity-low" aria-hidden="true"></i><span>${formatCopy("hex_legend_low", { count: `<strong data-hex-legend-lower>${formatSymptomaticReportCount(thresholds.lowerCount)}</strong>` })}</span></span>
    <span><i class="hex-poc-legend-quantity hex-poc-legend-quantity-middle" aria-hidden="true"></i><span>${formatCopy("hex_legend_middle", { lower: `<strong data-hex-legend-lower>${thresholds.lowerCount}</strong>`, upper: `<strong data-hex-legend-upper>${formatSymptomaticReportCount(thresholds.upperCount)}</strong>` })}</span></span>
    <span><i class="hex-poc-legend-quantity hex-poc-legend-quantity-high" aria-hidden="true"></i><span>${formatCopy("hex_legend_high", { count: `<strong data-hex-legend-upper>${formatSymptomaticReportCount(thresholds.upperCount)}</strong>` })}</span></span>
  `;
}

function setHexQuantityThreshold(name, value) {
  const result = quantityScale.setThreshold(name, value);
  syncHexQuantityThresholdControls();
  if (result.changed) setMapQuantityThresholds(result.state.thresholds);
}

function refreshQuantityScale(state) {
  const result = quantityScale.refresh({
    reports,
    datasetVersion: MOCK_REPORT_DATASET_VERSION,
    timeState: state,
    selectedSymptoms,
  });
  if (!result.recalculated) return false;
  if (screen.querySelector("[data-hex-quantity-threshold]")) {
    syncHexQuantityThresholdControls();
  }
  if (result.thresholdsChanged) setMapQuantityThresholds(result.state.thresholds);
  return true;
}

function syncHexQuantityThresholdControls() {
  const { maximum, thresholds } = quantityScale.state;
  const lowerSlider = screen.querySelector('[data-hex-quantity-threshold="lowerCount"]');
  const upperSlider = screen.querySelector('[data-hex-quantity-threshold="upperCount"]');
  lowerSlider.max = String(maximum);
  lowerSlider.value = String(thresholds.lowerCount);
  lowerSlider.setAttribute("aria-valuetext", formatSickReportCount(thresholds.lowerCount));
  upperSlider.max = String(maximum);
  upperSlider.value = String(thresholds.upperCount);
  upperSlider.setAttribute("aria-valuetext", formatSickReportCount(thresholds.upperCount));
  const quantityRange = screen.querySelector(".hex-poc-quantity-range-control");
  quantityRange.style.setProperty("--lower-stop", `${quantityThresholdPosition(thresholds.lowerCount, maximum)}%`);
  quantityRange.style.setProperty("--upper-stop", `${quantityThresholdPosition(thresholds.upperCount, maximum)}%`);
  screen.querySelectorAll("[data-hex-lower-threshold]").forEach((node) => {
    node.textContent = formatSickReportCount(thresholds.lowerCount);
  });
  screen.querySelectorAll("[data-hex-upper-threshold]").forEach((node) => {
    node.textContent = formatSickReportCount(thresholds.upperCount);
  });
  screen.querySelectorAll("[data-hex-legend-lower]").forEach((node, index) => {
    node.textContent = index === 0
      ? formatSymptomaticReportCount(thresholds.lowerCount)
      : String(thresholds.lowerCount);
  });
  screen.querySelectorAll("[data-hex-legend-upper]").forEach((node) => {
    node.textContent = formatSymptomaticReportCount(thresholds.upperCount);
  });
}

function quantityThresholdPosition(count, maximum) {
  return (count / maximum) * 100;
}

function formatSickReportCount(count) {
  return countCopy("hex_sick_report_one", "hex_sick_report_many", count);
}

function formatSymptomaticReportCount(count) {
  return countCopy("hex_symptomatic_report_one", "hex_symptomatic_report_many", count);
}

function overlaySwatchMarkup(category) {
  const config = HEX_OVERLAY_CATEGORIES[category];
  return `<i class="hex-poc-overlay-swatch" style="--hex-poc-overlay-color: ${config.color}" aria-hidden="true"></i>`;
}

function visibleReportCountMarkup(count = filteredHumanReports(timeController.state).length) {
  return countCopy("hex_human_report_one", "hex_human_report_many", count, { count: count.toLocaleString() });
}

function syncVisibleHumanReportCount(count) {
  screen.querySelectorAll("[data-hex-visible-count]").forEach((node) => {
    node.textContent = visibleReportCountMarkup(count);
  });
}

function syncHumanFilterState(state) {
  const reportCount = filteredHumanReports(state).length;
  syncVisibleHumanReportCount(reportCount);
  syncSymptomContext(reportCount);
  const timeSummary = screen.querySelector("[data-hex-time-summary]");
  if (timeSummary) {
    timeSummary.textContent = timeWindowSummaryText(state, reportCount);
  }
  syncHumanEmptyState(reportCount);
}

function filteredHumanReports(state) {
  return filterHexReportsBySymptoms(filterHexReportsByTime(reports, state), selectedSymptoms);
}

function syncSymptomContext(reportCount) {
  const context = screen.querySelector("[data-hex-symptom-context]");
  if (context) context.textContent = symptomContextText(reportCount);
}

function hexSymptomButtonsMarkup() {
  return HEX_POC_SYMPTOMS.map((symptom) => `
    <button type="button" data-hex-symptom="${escapeHexHtml(symptom)}" aria-pressed="${selectedSymptoms.includes(symptom)}">${escapeHexHtml(symptomLabel(symptom))}</button>
  `).join("");
}

function toggleHexSymptom(symptom) {
  if (!HEX_POC_SYMPTOMS.includes(symptom)) return false;
  const nextSymptoms = selectedSymptoms.includes(symptom)
    ? selectedSymptoms.filter((selected) => selected !== symptom)
    : [...selectedSymptoms, symptom];
  return setSelectedSymptoms(nextSymptoms);
}

function setSelectedSymptoms(nextSymptoms) {
  let normalized;
  try {
    normalized = normalizeHexSymptomSelection(nextSymptoms);
  } catch {
    return false;
  }
  const nextKey = hexSymptomSelectionKey(normalized);
  if (nextKey === selectedSymptomsKey) return false;
  selectedSymptoms = normalized;
  selectedSymptomsKey = nextKey;
  syncSymptomControls();
  refreshQuantityScale(timeController.state);
  syncHumanFilterState(timeController.state);
  renderMapForFilters();
  return true;
}

function syncSymptomControls() {
  screen.querySelectorAll("[data-hex-symptom]").forEach((button) => {
    button.setAttribute("aria-pressed", String(selectedSymptoms.includes(button.dataset.hexSymptom)));
  });
  const clearButton = screen.querySelector("[data-hex-clear-symptoms]");
  if (clearButton) clearButton.disabled = selectedSymptoms.length === 0;
}

function symptomContextText(count = filteredHumanReports(timeController.state).length) {
  const reports = countCopy("hex_report_one", "hex_report_many", count, { count: count.toLocaleString() });
  if (selectedSymptoms.length === 0) return `${reports} · ${copy("hex_no_symptom_filter")}`;
  if (selectedSymptoms.length === 1) return formatCopy("hex_containing", { reports, symptom: symptomLabel(selectedSymptoms[0]) });
  return formatCopy("hex_containing_all", { reports });
}

function syncHumanEmptyState(reportCount) {
  const emptyMessage = screen.querySelector("[data-hex-human-empty]");
  if (!emptyMessage) return;
  emptyMessage.hidden = reportCount !== 0;
  emptyMessage.textContent = emptyTimeMessage();
}

function timeWindowSummaryText(state, reportCount) {
  return formatCopy("hex_time_summary", {
    window: compactTimeWindowText(state),
    reports: countCopy("hex_report_one", "hex_report_many", reportCount, { count: reportCount.toLocaleString() }),
  });
}

function compactTimeWindowText(state) {
  const startLabel = formatDate(state.startDate);
  const endLabel = formatDate(state.selectedDate);
  if (state.startDate.slice(0, 4) !== state.selectedDate.slice(0, 4)) return `${startLabel}–${endLabel}`;
  return language === "ES" ? `${startLabel}–${endLabel}` : `${startLabel.replace(/, \d{4}$/, "")}–${endLabel}`;
}

function symptomLabel(symptom) {
  if (symptom === "Cough/congestion") return copy("hex_symptom_cough_congestion");
  const keys = {
    Nausea: "pulse_symptom_nausea",
    "Difficulty breathing": "pulse_symptom_difficulty_breathing",
    "Sore throat": "pulse_symptom_sore_throat",
    Rash: "pulse_symptom_rash",
    Fever: "pulse_symptom_fever",
    Chills: "pulse_symptom_chills",
    Diarrhea: "pulse_symptom_diarrhea",
    "Bleeding from body openings": "pulse_symptom_bleeding",
    "Red eyes": "pulse_symptom_red_eyes",
    "Body aches": "pulse_symptom_body_aches",
    "Discolored or bloody urine": "pulse_symptom_discolored_or_bloody_urine",
    "Loss of smell or taste": "pulse_symptom_loss_of_smell_or_taste",
    "Yellow skin/eyes": "pulse_symptom_yellow_skin_or_eyes",
  };
  return copy(keys[symptom]) || symptom;
}

function escapeHexHtml(value) {
  return String(value ?? "").replace(/[&<>'"]/g, (character) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    "'": "&#39;",
    "\"": "&quot;",
  })[character]);
}

function mapZoomDebugMarkup(mapId) {
  if (!debugMode) return "";
  return `
    <div class="map-debug map-zoom-debug" data-map-debug-zoom-for="${mapId}">
      <strong>Debug</strong>
      <span>Zoom: <span data-map-debug-zoom>--</span></span>
    </div>
  `;
}

function updateMapZoomDebug(mapId, zoom) {
  document.querySelectorAll(`[data-map-debug-zoom-for="${mapId}"] [data-map-debug-zoom]`).forEach((node) => {
    node.textContent = zoom.toFixed(2);
  });
}
